import { AssetCategory } from "./AssetCategory";

/**
 * The model types that each get their own side nav tab and Models panel,
 * keyed by the `assetPageType` they use in the address, e.g.
 * `/projects/12/scans`.
 *
 * The names people see are Models, Scans, GIS and Imagery.
 * `flyToTarget` is what the Fly to action names, e.g. "Fly to scan".
 */
export const modelPageTypes = {
  models: {
    category: AssetCategory.bim,
    label: "Models",
    itemNoun: "model",
    flyToTarget: "model",
    detailsTitle: "Model details",
  },
  scans: {
    category: AssetCategory.pointcloud,
    label: "Scans",
    itemNoun: "scan",
    flyToTarget: "scan",
    detailsTitle: "Scan details",
  },
  gis: {
    category: AssetCategory.gis,
    label: "GIS",
    itemNoun: "model",
    flyToTarget: "GIS",
    detailsTitle: "GIS model details",
  },
  imagery: {
    category: AssetCategory.scanset,
    label: "Imagery",
    itemNoun: "imagery",
    flyToTarget: "imagery",
    detailsTitle: "Imagery details",
  },
} as const;

export type ModelPageType = keyof typeof modelPageTypes;

export function isModelPageType(value: unknown): value is ModelPageType {
  return typeof value === "string" && Object.hasOwn(modelPageTypes, value);
}

/**
 * Whether an `assetPageType` shows models: one of the model type tabs, or
 * `other` for model types without a tab of their own.
 */
export function isModelsAssetPageType(value: unknown) {
  return value === "other" || isModelPageType(value);
}

/**
 * What a model is called on its pages, e.g. "Switch point cloud on". Model
 * types without a tab of their own are just a "model".
 */
export function getModelWording(assetPageType: unknown) {
  return isModelPageType(assetPageType)
    ? modelPageTypes[assetPageType]
    : { itemNoun: "model", detailsTitle: "Model details" };
}

export function getModelPageTypeForCategory(
  category: AssetCategory,
): ModelPageType | undefined {
  return (Object.keys(modelPageTypes) as ModelPageType[]).find(
    (pageType) => modelPageTypes[pageType].category === category,
  );
}
